import { inject, Injectable } from '@angular/core';
import { ActionEnum, CommentBase, CommentChat, SharedTagSystemNames } from '@shared/api/api-loop/models';
import { ConfirmService } from '@shared/shared/confirm/common/confirm.service';
import { SharedTagApiService } from '@shared/api/api-loop/services/shared-tag-api.service';
import { CommentApiService } from '@shared/api/api-loop/services';
import { SharedTagActionsService } from '@shared/modules/shared-tag/data-access/shared-tag-actions/shared-tag-actions.service';
import { ConversationActionService } from '@shared/services/data/conversation-action/conversation-action.service';
import { Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { CommentChatModel, CommentModel } from '@dta/shared/models-api-loop/comment/comment.model';
import { SharedTagSystemModel } from '@dta/shared/models-api-loop/shared-tag/shared-tag.model';

@Injectable({ providedIn: 'root' })
export class CommentOperationsService {
  private readonly confirmService: ConfirmService = inject(ConfirmService);
  private readonly sharedTagApiService: SharedTagApiService = inject(SharedTagApiService);
  private readonly commentApiService: CommentApiService = inject(CommentApiService);
  private readonly sharedTagActionsService: SharedTagActionsService = inject(SharedTagActionsService);
  private readonly conversationActionService: ConversationActionService = inject(ConversationActionService);

  changeCommentChat$(comment: CommentChat, newComment: string): Observable<CommentChat> {
    comment.comment = newComment;
    return this.commentApiService.Comment_UpdateChat({
      commentChat: comment
    });
  }

  private showConfirmMessageForDelete$(options?: { message?: string; description?: string }): Observable<unknown> {
    return this.confirmService.confirm({
      message: options?.message ?? `Delete comment`,
      description: options?.description ?? `Are you sure you want to delete this comment?`,
      confirmMessage: 'Delete',
      declineMessage: 'Cancel',
      confirmButton: {
        fill: 'color',
        color: 'danger'
      }
    });
  }

  deleteCommentWithConversationAction$(
    comment: CommentBase,
    options?: { message?: string; description?: string }
  ): Observable<unknown> {
    return this.showConfirmMessageForDelete$(options).pipe(
      switchMap(() => {
        const deleteTag = SharedTagSystemModel.buildSystemSharedTag(SharedTagSystemNames.DELETED);
        const commentModel = new CommentChatModel(comment);
        commentModel.setSharedTag(deleteTag);

        return this.conversationActionService
          .conversationAction({
            action: ActionEnum.DELETE_COMMENT,
            conversationIds: [comment.parent?.id],
            messageId: comment.id,
            isSharedAction: false
          })
          .pipe(map(() => commentModel));
      }),
      take(1),
      tap((commentModel: CommentModel) => {
        this.sharedTagActionsService.sharedTagsUpdated$.next(commentModel.sharedTags);
      })
    );
  }

  deleteComment$(comment: CommentBase, options?: { message?: string; description?: string }): Observable<unknown> {
    return this.showConfirmMessageForDelete$(options).pipe(
      switchMap(() => {
        const deleteTag = SharedTagSystemModel.buildSystemSharedTag(SharedTagSystemNames.DELETED);
        const commentModel = new CommentChatModel(comment);
        commentModel.setSharedTag(deleteTag);

        return this.sharedTagApiService
          .SharedTag_UpdateTag({
            tags: commentModel.sharedTags
          })
          .pipe(map(() => commentModel));
      }),
      take(1),
      tap((commentModel: CommentModel) => {
        this.sharedTagActionsService.sharedTagsUpdated$.next(commentModel.sharedTags);
      })
    );
  }
}
